/// <reference path="../../../../node_modules/@arcgis/core/interfaces.d.ts" />
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer.js';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer.js';
import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer.js';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol.js';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol.js';
import Color from '@arcgis/core/Color.js';
import { useEffect, useRef, useState } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import { findRenderableLayer,
	zoomToScale,
	updateVectorTileLayerPaint,
	setVectorTileColor,
	setVectorTileColorExpression,
	buildVtlSymbolColorByPlan,
	applyVtlStyleLayer,
	updateVtlIconImage,
	updateVectorTileLayerZoom,
	updateVectorTileLayerIconImage,
	pictureDataUrlFromSymbolLoader,
	getVtlIconSpriteColor,
	bubbleSymbolLayout,
	bubbleSymbolPaint,
} from '../utils/rendererHelpers';
import { setVectorTileLayerStyleLayerIfChanged } from '../utils/setVectorTileLayerStyleLayerIfChanged';
import {
	colorRangeToBands,
	colorExpressionToIconImageExpression,
} from '../utils/colorExpressions';
import type CompositeLayer from '../layers/CompositeLayer';
import type EsriLayerManager from '../layers/EsriLayerManager';
import SymbolLoader from '@Map/symbols/SymbolLoader';
import logger from '@Map/logger/Logger';
import ThemeManager, { THEME_NOT_EXIST } from '@Map/services/ThemeManager';
import { fallbackDefaultThemeId } from '@Map/services/ThemeDefaults';
import type { ThemeEndpoint } from '@Map/services/types';
import {
	ColorByOptions,
	type ThemeList,
	type ConfigLayer,
	type ConfigLayerInternal,
	type ThemeSettings,
} from '@Map/panel/types';
import {
	NON_PERSISTABLE_LAYER_FIELDS,
	stripLayerFields,
} from '@Map/panel/themeFields';
import { buildThemeSeedLayers } from '../utils/buildThemeSeedLayers';
import type { AttributeColorConfig, ColorRangeConfig } from '../types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type RenderableLayer = __esri.GeoJSONLayer | __esri.FeatureLayer;

/**
 * Returns the correct symbol class for a layer, preserving the existing
 * symbol type (line → SimpleLineSymbol, fill → SimpleFillSymbol, else marker).
 */
function buildSymbol(layer: RenderableLayer, color: string): __esri.Symbol {
	const esriColor = new Color(color);
	const currentSymbol = (layer.renderer as __esri.SimpleRenderer)?.symbol;

	if (currentSymbol?.type === 'simple-line') {
		return new SimpleLineSymbol({
			color: esriColor,
			width: (currentSymbol as __esri.SimpleLineSymbol).width ?? 2,
		});
	}
	if (currentSymbol?.type === 'simple-fill') {
		return new SimpleFillSymbol({ color: esriColor });
	}
	return new SimpleMarkerSymbol({ color: esriColor, size: 8 });
}

/**
 * Rebuilds the PictureMarkerSymbol on a GeoJSON point layer using a freshly
 * rasterised PNG data URL. Fire-and-forget — the caller doesn't need to await.
 */
function applyGeoJsonIcon(
	layer: RenderableLayer,
	iconName: string,
	color: string,
	getIconDataUrl: (icon: string, color: string, disableBackground?: boolean) => Promise<string>,
	disableBackground?: boolean,
): void {
	getIconDataUrl(iconName, color, disableBackground).then(url => {
		if (!url) return;
		const size = ((layer.renderer as SimpleRenderer)?.symbol as any)?.width ?? 24;
		layer.renderer = new SimpleRenderer({
			symbol: new PictureMarkerSymbol({ url, width: size, height: size }),
		});
	});
}

/** Type for a function that builds a Mapbox-style color expression for a tile VectorTileLayer style layer.
 * Optional builder that returns a Mapbox-style color expression for a tile
 * VectorTileLayer style layer when the attribute lives in an external
 * id → value lookup (Info360 tile services). Returns null when no expression
 * can yet be built (lookup pending) or when the layer is not a tile layer.
*/
type TileColorExpressionBuilder = (
	layerId: string,
	edits: Partial<ConfigLayer>,
	fallbackColor: string,
) => unknown[] | null;

/** Predicate: true when a layer's colour-by lookup is configured but not yet loaded. */
type TileLookupPendingPredicate = (
	layerId: string,
	edits: Partial<ConfigLayer>,
) => boolean;

/** Atlas-first sprite pre-build: ensures the supplied synthetic style layers' sprites exist. */
type EnsureSymbolSprites = (
	layerId: string,
	syntheticStyleLayers: any[],
) => Promise<void>;

const TILE_FALLBACK_COLOR = '#768d95';

/**
 * Sprite icon-name used to render a sensor colour-by-range layer "as a heatmap"
 * (the Theme Editor toggle sets layer `type` to `'bubble'`). The sensor symbol
 * layer's icon sprite is swapped for this round disc — recoloured per value band
 * by the same machinery as ordinary colour-by — mirroring the Mapbox bubble
 * layer. Esri's VectorTileLayer only renders symbol layers (not `circle`/
 * `heatmap` style layers), so the "bubble" is a circular symbol sprite.
 */
const BUBBLE_ICON_NAME = 'circle';

/** Backgroundless glow disc sprite id used for the heatmap "bubble" base. */
function bubbleGlowSpriteId(): string {
	return SymbolLoader.createSymbolId(
		BUBBLE_ICON_NAME,
		TILE_FALLBACK_COLOR,
		false,
		undefined,
		true,
	);
}

/**
 * Creates a batched, debounced sprite-refresh scheduler.
 *
 * Instead of calling `refreshSprites` immediately on every change, callers
 * invoke `schedule(layerId)`.  The scheduler accumulates unique layer IDs and
 * only flushes them (calling `refreshFn` once per unique ID) after `delayMs`
 * of inactivity — preventing redundant refreshes when many layers change in
 * quick succession.
 */
function createBatchedDebouncedRefresh(
	refreshFn: (layerId: string) => Promise<void>,
	delayMs: number,
): { schedule: (layerId: string) => void; cancel: () => void } {
	const pendingIds = new Set<string>();
	let timer: ReturnType<typeof setTimeout> | null = null;

	const flush = () => {
		const ids = Array.from(pendingIds);
		pendingIds.clear();
		timer = null;
		logger.debug('[SpriteRefresh] Flushing batched refresh', { layerIds: ids });
		ids.forEach(id => refreshFn(id));
	};

	return {
		schedule: (layerId: string) => {
			pendingIds.add(layerId);
			if (timer !== null) clearTimeout(timer);
			timer = setTimeout(flush, delayMs);
		},
		cancel: () => {
			if (timer !== null) {
				clearTimeout(timer);
				timer = null;
			}
			pendingIds.clear();
		},
	};
}

/**
 * Reverts a style layer to its captured base spec. Used during theme switch
 * to clear the outgoing theme's paint/layout overrides from layers the
 * incoming theme does not touch. Uses the snapshot CompositeLayer captured
 * on first style-layer access — a complete replacement, not a key-by-key
 * merge.
 *
 * No-op when the composite has no snapshot for the id (style layer was
 * never resolved before the theme change). The incoming theme's edits, if
 * any, will determine final state.
 */
function restoreLayerToBaseStyle(
	composite: CompositeLayer,
	styleLayerId: string,
): boolean {
	const snapshot = composite.getBaseSnapshot(styleLayerId);
	if (!snapshot) return false;
	const vtl = composite.findVectorTileLayer(styleLayerId);
	if (!vtl) return false;
	return setVectorTileLayerStyleLayerIfChanged(vtl, snapshot, {
		source: 'restoreLayerToBaseStyle',
	});
}

/**
 * Translates a ConfigEdits layer entry into Esri renderer calls.
 * Called when loading or switching themes.
 */
function applyLayerEditsToMap(
	view: __esri.MapView,
	layerId: string,
	edits: Partial<ConfigLayer>,
	buildTileColorExpression?: TileColorExpressionBuilder,
	symbolLoader?: SymbolLoader | null,
	scheduleRefreshSprites?: (layerId: string) => void,
	getIconDataUrl?: (icon: string, color: string, disableBackground?: boolean) => Promise<string>,
	isTileLookupPending?: TileLookupPendingPredicate,
	ensureSymbolSprites?: EnsureSymbolSprites,
	callerLabel?: string,
): void {
	logger.debug(`[applyLayerEditsToMap] called by: ${callerLabel ?? 'unknown'}`, { layerId });
	const layer = findRenderableLayer(view, layerId);

	if (layer) {
		if (edits.colorBy === 'attribute' && edits.attribute && edits.colorRange?.length) {
			const uniqueValueInfos = edits.colorRange.map(({ value, color }) => ({
				value,
				symbol: buildSymbol(layer, color) as any,
			}));
			layer.renderer = new UniqueValueRenderer({
				field: edits.attribute,
				uniqueValueInfos,
				defaultSymbol: buildSymbol(layer, '#768d95') as any,
			});
		} else if (edits.colorBy === 'range' && edits.attribute && edits.colorRange?.length) {
			const classBreakInfos = colorRangeToBands(edits.colorRange).map(
				({ min, max, color }) => ({
					minValue: min,
					maxValue: max,
					symbol: buildSymbol(layer, color) as any,
				}),
			);
			layer.renderer = new ClassBreaksRenderer({
				field: edits.attribute,
				classBreakInfos,
				defaultSymbol: buildSymbol(layer, '#768d95') as any,
			});
		} else if (edits.color) {
			layer.renderer = new SimpleRenderer({ symbol: buildSymbol(layer, edits.color) as any });
		}

		if (edits.icon) {
			// Rebuild the PictureMarkerSymbol for GeoJSON point layers.
			// Color comes from the edit or falls back to the current symbol color.
			const color =
				edits.color ??
				(((layer.renderer as SimpleRenderer)?.symbol as any)?.color
					? new Color(((layer.renderer as SimpleRenderer)?.symbol as any).color).toHex()
					: undefined);
		if (color && getIconDataUrl) {
			applyGeoJsonIcon(layer, edits.icon, color, getIconDataUrl, edits.iconBackground === false);
		}
		}

		if (edits.lineWidth != null) {
			const renderer = layer.renderer as __esri.SimpleRenderer;
			if (renderer?.symbol) {
				const symbol = renderer.symbol.clone() as __esri.SimpleLineSymbol;
				if ('width' in symbol) {
					symbol.width = edits.lineWidth;
					layer.renderer = new SimpleRenderer({ symbol: symbol as any });
				}
			}
		}

		if (edits.icon && SymbolLoader.iconValid(edits.icon) && symbolLoader) {
			const color = edits.color ?? (layer.renderer as __esri.SimpleRenderer)?.symbol?.color?.toHex?.() ?? '#768d95';
			const symbolId = SymbolLoader.createSymbolId(edits.icon, color);
			pictureDataUrlFromSymbolLoader(symbolLoader, symbolId).then(url => {
				if (url) {
					layer.renderer = new SimpleRenderer({
						symbol: new PictureMarkerSymbol({ url, width: 20, height: 20 }) as any,
					});
				}
			}).catch((err: unknown) => {
				logger.error(`Failed to load icon image for layer ${layerId}`, { error: String(err) });
			});
		}
	} else {
		// Tile-service layers: update VectorTileLayer style paint properties.
		// Vector tile features expose only `id`; the attribute (e.g.
		// `nominalDiameter`) is resolved through ConfigAdaptor's id → value
		// lookup. buildTileColorExpression returns a `match` keyed on `id`.
		// Falls back to a raw `['get', attribute]` expression for services
		// where the attribute is encoded into the tile features themselves.
		const colorByActive =
			(edits.colorBy === 'range' || edits.colorBy === 'attribute') &&
			edits.attribute &&
			edits.colorRange?.length;
		if (colorByActive) {
			const fallback = edits.color ?? TILE_FALLBACK_COLOR;
			// Resolve the colour expression: a `match` keyed on feature `id`
			// (id → value lookup) when available, else a `step`/`match` on the
			// tile feature's own attribute value.
			const builtExpr: unknown = buildTileColorExpression?.(
				layerId,
				edits,
				fallback,
			);
			// A symbol layer whose lookup is still loading must be left untouched:
			// applying the degenerate fallback would point icon-image at sprites
			// not yet in the atlas (blanking the icons). LOOKUP_COMPLETE →
			// reapplyAllPendingEdits re-runs this once the real `match` is ready.
			const lookupPending =
				!builtExpr && !!isTileLookupPending?.(layerId, edits);
			let colorExpr: unknown = builtExpr;
			if (!colorExpr) {
				if (edits.colorBy === 'range') {
					const stepExpr: unknown[] = [
						'step',
						['get', edits.attribute as string],
						fallback,
					];
					(edits.colorRange ?? []).forEach(({ value, color }) =>
						stepExpr.push(Number(value), color),
					);
					colorExpr = stepExpr;
				} else {
					const matchExpr: unknown[] = [
						'match',
						['get', edits.attribute as string],
					];
					(edits.colorRange ?? []).forEach(({ value, color }) =>
						matchExpr.push(value, color),
					);
					matchExpr.push(fallback);
					colorExpr = matchExpr;
				}
			}
			// Line/fill layers carry a paint colour key. Symbol (icon) layers do
			// not, so setVectorTileColorExpression returns false — colour those by
			// swapping the icon sprite per range/value instead.
			const appliedAsPaint = setVectorTileColorExpression(
				view,
				layerId,
				colorExpr,
			);
			// "Show as a heatmap" (type === 'bubble') is owned end-to-end by the
			// single producer (resolveSensorStyleLayer / applySensorStyleNow /
			// the build-time sensor resolver) — skip the diamond colour-by here
			// so this path can never clobber the glow.
			if (!appliedAsPaint && !lookupPending && edits.type !== 'bubble') {
				// Symbol (icon) layer with a usable expression. Build the
				// recoloured band sprites into the atlas FIRST, then flip the live
				// icon-image — so icons transition sprite→sprite with no blank
				// frame and no extra tile re-fetch. Falls back to the legacy
				// set-then-debounced-refresh order when no atlas-first helper wired.
				const plan = buildVtlSymbolColorByPlan(view, layerId, colorExpr);
				if (plan) {
					if (ensureSymbolSprites) {
						ensureSymbolSprites(layerId, [plan.nextStyleLayer])
							.then(() =>
								applyVtlStyleLayer(view, layerId, plan.nextStyleLayer),
							)
							.catch((err: unknown) =>
								logger.error('atlas-first symbol colour-by failed', {
									layerId,
									error: String(err),
								}),
							);
					} else {
						const changed = applyVtlStyleLayer(
							view,
							layerId,
							plan.nextStyleLayer,
						);
						if (changed) scheduleRefreshSprites?.(layerId);
					}
				}
			} else if (!appliedAsPaint && lookupPending) {
				logger.debug(
					'[applyLayerEditsToMap] symbol lookup pending — deferring colour-by',
					{ layerId },
				);
			}
		} else if (edits.color) {
			setVectorTileColor(view, layerId, edits.color);
		}
		if (edits.lineWidth != null) {
			updateVectorTileLayerPaint(view, layerId, {
				'line-width': edits.lineWidth,
			});
		}

		// Apply all icon-image layout changes in a single VTL pass, then
		// regenerate the sprite sheet once if anything changed. Skip the fixed
		// colour when colouring by attribute/range — the data-driven sprite
		// colouring above owns icon-image and must not be flattened to one colour.
		const iconOpts: Parameters<typeof updateVtlIconImage>[2] = {};
		// Never touch icon-image for a bubble layer — the sensor resolver owns it.
		if (edits.type !== 'bubble') {
			if (edits.color && !colorByActive) iconOpts.color = edits.color;
			if (edits.icon) iconOpts.iconName = edits.icon;
			if (edits.iconBackground != null)
				iconOpts.disableBackground = !edits.iconBackground;
		}
		if (Object.keys(iconOpts).length > 0) {
			const changed = updateVtlIconImage(view, layerId, iconOpts);
			if (changed) scheduleRefreshSprites?.(layerId);
		}
	}

	if (edits.minZoom != null || edits.maxZoom != null) {
		// For GeoJSON/FeatureLayer: set minScale/maxScale on the map layer.
		// For VTL style layers: set minzoom/maxzoom on the style layer spec —
		// findLayerById won't find a style-layer ID, and even if it found the
		// parent VTL, minScale/maxScale would affect ALL sub-layers.
		if (layer) {
			if (edits.minZoom != null) (layer as any).minScale = zoomToScale(edits.minZoom);
			if (edits.maxZoom != null) (layer as any).maxScale = zoomToScale(edits.maxZoom);
		} else {
			updateVectorTileLayerZoom(
				view,
				layerId,
				edits.minZoom ?? null,
				edits.maxZoom ?? null,
			);
		}
	}
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Sprite-sheet helpers provided by `useLayers` and forwarded into the theme. */
export const useTheme = (
	themeEndpoint?: ThemeEndpoint | null,
	defaultThemeId?: string,
	buildTileColorExpression?: TileColorExpressionBuilder,
	symbolLoader?: SymbolLoader | null,
	refreshSprites?: (layerId: string) => Promise<void>,
	getIconDataUrl?: (icon: string, color: string, disableBackground?: boolean) => Promise<string>,
	manager?: EsriLayerManager | null,
	isTileLookupPending?: TileLookupPendingPredicate,
	ensureSymbolSprites?: EnsureSymbolSprites,
) => {
	const { view } = useEsriMapContext();
	// Keep a ref to the latest builder so internal calls always see the most
	// recent closure without rebuilding the API on every render.
	const buildTileColorExpressionRef = useRef(buildTileColorExpression);
	buildTileColorExpressionRef.current = buildTileColorExpression;
	// Predicate + atlas-first sprite pre-build from useLayers, held in refs so
	// internal calls always see the latest closure without rebuilding the API.
	const isTileLookupPendingRef = useRef(isTileLookupPending);
	isTileLookupPendingRef.current = isTileLookupPending;
	const ensureSymbolSpritesRef = useRef(ensureSymbolSprites);
	ensureSymbolSpritesRef.current = ensureSymbolSprites;
	const symbolLoaderRef = useRef(symbolLoader);
	symbolLoaderRef.current = symbolLoader;
	// Layer manager — used by changeTheme to look up composites for snapshot
	// revert. Held in a ref so changes during a session take effect without
	// rebuilding the theme API on every render.
	const layerManagerRef = useRef(manager ?? null);
	layerManagerRef.current = manager ?? null;
	const themeManagerRef = useRef<ThemeManager | null>(null);

	// Keep the latest refreshSprites callback in a ref so the scheduler always
	// calls the most recent version without being recreated on every render.
	const refreshSpritesRef = useRef(refreshSprites);
	refreshSpritesRef.current = refreshSprites;

	// Single debounced scheduler for the lifetime of this hook instance.
	// Accumulates changed layer IDs and flushes them after 3 s of inactivity
	// so the same layer is never refreshed more than once per burst.
	const spriteSchedulerRef = useRef<{ schedule: (layerId: string) => void; cancel: () => void } | null>(null);
	if (!spriteSchedulerRef.current) {
		spriteSchedulerRef.current = createBatchedDebouncedRefresh(
			(id: string) => refreshSpritesRef.current?.(id) ?? Promise.resolve(),
			2000,
		);
	}

	useEffect(() => {
		return () => {
			spriteSchedulerRef.current?.cancel();
		};
	}, []);

	// React state — drives LayerPanel dropdown and editor re-renders
	const [themes, setThemes] = useState<ThemeList>([]);
	const [selectedThemeId, setSelectedThemeId] = useState<string>(fallbackDefaultThemeId);
	const [isUserTheme, setIsUserTheme] = useState<boolean>(true);

	// Track theme name in hook state so it auto-populates on theme switch.
	// themeNameRef mirrors the state so saveTheme() always reads the latest
	// value even if called inside a stale closure before a re-render.
	const [themeName, _setThemeName] = useState<string>('');
	const themeNameRef = useRef<string>('');
	const setThemeName = (name: string) => {
		themeNameRef.current = name;
		_setThemeName(name);
	};

	// Track creating state so isDeletable can be derived reactively.
	const [isCreating, setIsCreating] = useState<boolean>(false);

	// Track theme settings (shared/personal toggle for editAll mode).
	const [themeSettings, setThemeSettings] = useState<ThemeSettings>({ shared: false });

	// Accumulates unsaved edits (mirrors ConfigAdaptor._configEdits in Mapbox).
	// pendingEditsRef is used by saveTheme (avoids stale closure).
	// pendingEdits state mirrors it so EsriMapInner can derive the display config.
	const pendingEditsRef = useRef<{ layers: Record<string, Partial<ConfigLayer>> }>({ layers: {} });
	const [pendingEdits, setPendingEdits] = useState<Record<string, Partial<ConfigLayer>>>({});

	// Tracks the most recently fully-loaded theme edits (initial restore or changeTheme).
	// Changes only on full theme loads, not on incremental user edits, so ReactEsriMap
	// can watch this to push the complete edit set into ConfigAdaptor for correct iconImage.
	const [loadedThemeEdits, setLoadedThemeEdits] = useState<Record<string, Partial<ConfigLayer>>>({});

	/** Sync helper — always call instead of mutating pendingEditsRef directly. */
	const setPendingLayers = (layers: Record<string, Partial<ConfigLayer>>) => {
		pendingEditsRef.current = { layers };
		setPendingEdits(layers);
	};

	// React refs don't trigger re-renders or effect re-runs when they change, so
	// Effect 2 (which reads themeManagerRef to restore the saved theme) would never
	// fire after Effect 1 populates the ref. This epoch counter bridges that gap:
	// Effect 1 increments it each time a ThemeManager is created (including
	// recreations on token/url change), and Effect 2 lists it as a dependency so
	// it runs on every creation — not just the first. Using a boolean (previously
	// themeManagerReady) fails on recreations because false→true→false→true nets
	// to no change across a single React commit, preventing Effect 2 from re-running
	// and leaving the new manager's _themes cache empty (silent save no-op).
	const [managerEpoch, setManagerEpoch] = useState(0);

	// isDeletable mirrors ThemeManager.isDeletable():
	// false if current theme is the default or if currently creating a new theme.
	const isDeletable = selectedThemeId !== fallbackDefaultThemeId && !isCreating;

	// -------------------------------------------------------------------------
	// Effect 1: create ThemeManager and fetch theme list
	// Keyed on endpoint only — does not touch `view` or apply renderers.
	// -------------------------------------------------------------------------
	useEffect(() => {
		if (!themeEndpoint?.url) return;

		const manager = new ThemeManager(
			themeEndpoint.url,
			themeEndpoint.token,
			null,
			defaultThemeId ?? fallbackDefaultThemeId,
		);
		themeManagerRef.current = manager;
		// Increment epoch so Effect 2 re-runs and re-warms the _themes cache.
		// A boolean flag (previously themeManagerReady) fails on manager
		// recreations: false→true→false→true nets to no change across a single
		// React commit and Effect 2 never re-fires, leaving the new cache cold.
		setManagerEpoch(e => e + 1);

		// Reset to default theme when the saved ID is no longer in the list.
		// Also call useDefaultTheme() so manager._themeId is updated (not just
		// React state), and bump the epoch so Effect 2 re-warms the default
		// theme's client — matching ConfigAdaptor's THEME_NOT_EXIST handling.
		manager.on(THEME_NOT_EXIST, () => {
			manager.useDefaultTheme();
			// Read back what the manager actually chose — useDefaultTheme may have
			// fallen back to the first available theme if the configured default
			// wasn't in the list (synthetic "1" sentinel case).
			setSelectedThemeId(
				manager.getSelectedThemeId() ??
					defaultThemeId ??
					fallbackDefaultThemeId,
			);
			setManagerEpoch(e => e + 1);
		});

		// Fetch the theme list — feeds the LayerPanel dropdown
		manager.getThemeList().then(list => {
			if (list?.length) setThemes(list);
		});

		return () => {
			manager.destroy();
			themeManagerRef.current = null;
			// No need to reset managerEpoch — the next creation will increment it.
		};
	}, [themeEndpoint?.url, themeEndpoint?.token, defaultThemeId]);

	// -------------------------------------------------------------------------
	// Effect 2: restore saved theme once `view` is available
	// Esri's MapView initialises asynchronously, so `view` is null on the first
	// render. Splitting here ensures pendingEdits and map renderers are seeded
	// from the backend theme as soon as the view is ready.
	// -------------------------------------------------------------------------
	useEffect(() => {
		const manager = themeManagerRef.current;
		if (!manager || !view) return;

		const savedId = manager.getSelectedThemeId() ?? defaultThemeId;
		if (!savedId) return;
		manager.getById(savedId).then(themeProps => {
			if (!themeProps?.configEdits) return;
			setSelectedThemeId(savedId);
			const isUserTheme = themeProps.isUserTheme ?? true;
			setIsUserTheme(isUserTheme);
			setThemeSettings({ shared: !isUserTheme });
			setThemeName(typeof themeProps.name === 'string' ? themeProps.name : '');
			setPendingLayers({ ...themeProps.configEdits.layers });
			setLoadedThemeEdits({ ...themeProps.configEdits.layers });
			Object.entries(themeProps.configEdits.layers).forEach(([layerId, edits]) => {
				applyLayerEditsToMap(
					view,
					layerId,
					edits,
					buildTileColorExpressionRef.current,
					symbolLoaderRef.current,
					undefined,
					undefined,
					isTileLookupPendingRef.current,
					ensureSymbolSpritesRef.current,
					'restoreSavedTheme (Effect 2)',
				);
			});
		});
	}, [view, managerEpoch]);

	// -------------------------------------------------------------------------
	// Sensor heatmap "bubble" — SINGLE producer + applier
	// -------------------------------------------------------------------------

	/**
	 * The single source of truth for the sensor heatmap "bubble" style. Given a
	 * PRISTINE base style layer (the creator's diamond — never the live, already
	 * modified style), returns the fully-resolved glow style for a layer with a
	 * pending `type:'bubble'` edit: the backgroundless glow disc recoloured per
	 * value band (or the uniform glow while the id→value lookup is still pending)
	 * plus the bubble layout/paint. Returns `null` for any non-bubble / non-symbol
	 * layer (built/recoloured normally by the Gap B diamond path).
	 *
	 * Always rebuilding from the pristine base makes this idempotent — repeated
	 * runs (build-time, toggle, lookup-complete, reload) can't compound, so there
	 * is exactly one authority and no diamond↔glow race.
	 */
	const resolveSensorStyleLayer = (
		layerId: string,
		styleLayer: Record<string, any>,
	): Record<string, any> | null => {
		const edit = pendingEditsRef.current.layers[layerId];
		if (edit?.type !== 'bubble') return null;
		if (styleLayer?.type !== 'symbol') return null;
		const glow = bubbleGlowSpriteId();
		const fallback = edit.color ?? TILE_FALLBACK_COLOR;
		const colorExpr = buildTileColorExpressionRef.current?.(
			layerId,
			edit,
			fallback,
		);
		const iconImage = colorExpr
			? colorExpressionToIconImageExpression(colorExpr, glow)
			: glow; // uniform glow until the lookup resolves
		return {
			...styleLayer,
			layout: {
				...(styleLayer.layout ?? {}),
				'icon-image': iconImage,
				...bubbleSymbolLayout(edit.scale),
			},
			paint: {
				...(styleLayer.paint ?? {}),
				...bubbleSymbolPaint(edit.fillOpacity),
			},
			// The producer owns the layer's full style, so it must carry the
			// pending visibility range too — otherwise this async re-apply would
			// overwrite the minzoom/maxzoom that setVisibilityRange just set
			// (zoom-level slider). Falls back to the pristine/config values.
			...(edit.minZoom != null ? { minzoom: edit.minZoom } : {}),
			...(edit.maxZoom != null ? { maxzoom: edit.maxZoom } : {}),
		};
	};

	/**
	 * Applies the current sensor style immediately, without a full `loadStyle`
	 * (atlas-first `setStyleLayer`). Always works from the composite's PRISTINE
	 * style layer so toggling never compounds and toggle-off cleanly drops the
	 * glow layout/paint. For `type:'bubble'` it applies the resolved glow; for
	 * `type:'symbol'` it restores the pristine diamond and runs the Gap B
	 * colour-by on it.
	 */
	const applySensorStyleNow = (layerId: string): void => {
		if (!view) return;
		const composite = layerManagerRef.current?.compositeLayers.find(
			c => c.id === layerId,
		);
		const pristine = composite?.styleLayer as
			| Record<string, any>
			| undefined;
		const edit = pendingEditsRef.current.layers[layerId] ?? {};
		if (edit.type === 'bubble') {
			if (!pristine) return;
			const resolved = resolveSensorStyleLayer(layerId, pristine);
			if (!resolved) return;
			const ensure = ensureSymbolSpritesRef.current;
			if (ensure) {
				ensure(layerId, [resolved])
					.then(() => applyVtlStyleLayer(view, layerId, resolved))
					.catch((err: unknown) =>
						logger.error('bubble apply failed', {
							layerId,
							error: String(err),
						}),
					);
			} else {
				applyVtlStyleLayer(view, layerId, resolved);
			}
			return;
		}
		// type === 'symbol' (toggle-off): restore the pristine diamond shape from
		// the creator (NOT a snapshot), then re-apply the Gap B colour-by on it.
		if (pristine) applyVtlStyleLayer(view, layerId, pristine);
		applyLayerEditsToMap(
			view,
			layerId,
			{ ...edit, type: 'symbol' },
			buildTileColorExpressionRef.current,
			symbolLoaderRef.current,
			spriteSchedulerRef.current?.schedule,
			getIconDataUrl,
			isTileLookupPendingRef.current,
			ensureSymbolSpritesRef.current,
			'applySensorStyleNow',
		);
	};

	// -------------------------------------------------------------------------
	// Returned API
	// -------------------------------------------------------------------------
	return {
		// State for LayerPanel props
		themes,
		selectedThemeId,
		themeName,
		themeSettings,
		isDeletable,

		// Expose setters for EsriMapInner callbacks
		setThemeName,
		setThemeSettings,

		// ------------------------------------------------------------------
		// Renderer methods — called immediately on every editor change
		// ------------------------------------------------------------------

		setFixedColor(layerId: string, color: string): void {
			if (!view) return;
			const layer = findRenderableLayer(view, layerId);
			if (layer) {
				layer.renderer = new SimpleRenderer({ symbol: buildSymbol(layer, color) as any });
			} else {
				setVectorTileColor(view, layerId, color);
				// Update color-encoded sprite IDs and rebuild the atlas.
				updateVtlIconImage(view, layerId, { color });
				spriteSchedulerRef.current?.schedule(layerId);
			}
		},

		/** Updates the icon on a layer (VTL: patches sprite ID; GeoJSON: re-rasterises PNG). */
		setIcon(layerId: string, iconName: string, color?: string, iconBackground?: boolean): void {
			if (!view) return;
			const layer = findRenderableLayer(view, layerId);
			if (layer) {
				if (color && getIconDataUrl) {
					applyGeoJsonIcon(layer, iconName, color, getIconDataUrl, iconBackground === false);
				}
			} else {
				const changed = updateVtlIconImage(view, layerId, { iconName });
				if (changed) spriteSchedulerRef.current?.schedule(layerId);
			}
		},

		/**
		 * Toggles the `-nobackground` suffix on VTL variant style layers.
		 * For GeoJSON layers, callers should use `setIcon` directly (icon + color
		 * are required to re-rasterise the PictureMarkerSymbol).
		 */
		setIconBackground(layerId: string, iconBackground: boolean): void {
			if (!view) return;
			if (findRenderableLayer(view, layerId)) return; // GeoJSON — handled via setIcon
			const changed = updateVtlIconImage(view, layerId, { disableBackground: !iconBackground });
			if (changed) spriteSchedulerRef.current?.schedule(layerId);
		},

		setAttributeColor(layerId: string, config: AttributeColorConfig): void {
			if (!view) return;
			applyLayerEditsToMap(
				view,
				layerId,
				{
					colorBy: ColorByOptions.attribute,
					attribute: config.field,
					colorRange: config.values.map(({ value, color }) => ({
						value: value as any,
						color,
					})),
				},
				buildTileColorExpressionRef.current,
				undefined,
				undefined,
				undefined,
				isTileLookupPendingRef.current,
				ensureSymbolSpritesRef.current,
				'setAttributeColor',
			);
		},

		setColorByRange(layerId: string, config: ColorRangeConfig): void {
			if (!view) return;
			applyLayerEditsToMap(
				view,
				layerId,
				{
					colorBy: ColorByOptions.range,
					attribute: config.field,
					colorRange: config.breaks.map(({ min, color }) => ({
						value: min,
						color,
					})),
				},
				buildTileColorExpressionRef.current,
				undefined,
				undefined,
				undefined,
				isTileLookupPendingRef.current,
				ensureSymbolSpritesRef.current,
				'setColorByRange',
			);
		},

		/**
		 * Switches a sensor (range-forced) layer between its asset icon
		 * (`'symbol'`) and the colour-by "bubble"/heatmap rendering (`'bubble'`),
		 * driven by the Theme Editor "Show as a heatmap" toggle. Updates pending
		 * edits (synchronously, via setPendingLayers) then applies the resolved
		 * style immediately via the single producer (`applySensorStyleNow`). The
		 * same producer is also baked into the built style and re-run on reload /
		 * lookup-complete, so there is one authority and no diamond↔glow race.
		 */
		setSymbolDisplayMode(
			layerId: string,
			mode: 'symbol' | 'bubble',
			scale?: number,
		): void {
			if (!view) return;
			const merged: Partial<ConfigLayer> = {
				...pendingEditsRef.current.layers[layerId],
				type: mode,
			};
			if (scale != null) merged.scale = scale;
			setPendingLayers({
				...pendingEditsRef.current.layers,
				[layerId]: merged,
			});
			applySensorStyleNow(layerId);
		},

		/**
		 * Build-time hook (called by EsriLayerManager while assembling the style
		 * handed to `loadStyle`): returns the resolved glow style for a layer with
		 * a pending `type:'bubble'` edit so the loaded style is BORN as the glow
		 * (no asset-icon flicker on reload, survives save), else `null`. Same
		 * single producer used by the immediate `applySensorStyleNow` path.
		 */
		resolveSensorStyleLayer: resolveSensorStyleLayer,

		setStrokeWidth(layerId: string, width: number): void {
			if (!view) return;
			const layer = findRenderableLayer(view, layerId);
			if (layer) {
				const renderer = layer.renderer as __esri.SimpleRenderer;
				if (!renderer?.symbol) return;
				const symbol = renderer.symbol.clone() as __esri.SimpleLineSymbol;
				if ('width' in symbol) symbol.width = width;
				layer.renderer = new SimpleRenderer({ symbol: symbol as any });
			} else {
				updateVectorTileLayerPaint(view, layerId, { 'line-width': width });
			}
		},

		resetLayerIcon(layerId: string, icon: string, color?: string): void {
			if (!view) return;
			if (!SymbolLoader.iconValid(icon)) return;
			const symbolId = SymbolLoader.createSymbolId(icon, color ?? TILE_FALLBACK_COLOR);
			updateVectorTileLayerIconImage(view, layerId, symbolId);
		},


		setVisibilityRange(layerId: string, minZoom: number | null, maxZoom: number | null): void {
			if (!view) return;
			const renderableLayer = findRenderableLayer(view, layerId);
			if (renderableLayer) {
				if (minZoom !== null) (renderableLayer as any).minScale = zoomToScale(minZoom);
				if (maxZoom !== null) (renderableLayer as any).maxScale = zoomToScale(maxZoom);
			} else {
				updateVectorTileLayerZoom(view, layerId, minZoom, maxZoom);
			}
		},

		setBlendMode(layerId: string, mode: string): void {
			if (!view) return;
			const layer = view.map?.findLayerById(layerId);
			if (layer && 'blendMode' in layer) {
				(layer as any).blendMode = mode;
			}
		},

		/**
		 * Applies a full set of layer edits (colorBy, color, lineWidth, etc.)
		 * directly to the map renderer. Use this to programmatically drive
		 * renderers without going through the Layer Panel theme editor.
		 */
		applyLayerTheme(layerId: string, edits: Partial<ConfigLayer>): void {
			if (!view) return;
			applyLayerEditsToMap(
				view,
				layerId,
				edits,
				buildTileColorExpressionRef.current,
				symbolLoaderRef.current,
				spriteSchedulerRef.current?.schedule,
				getIconDataUrl,
				isTileLookupPendingRef.current,
				ensureSymbolSpritesRef.current,
				'applyLayerTheme',
			);
		},

		/**
		 * Re-applies all pending color-by edits to the map. Intended to be
		 * called after an attribute Lookup completes, so tile-layer color
		 * expressions that depend on id → value mappings can be rebuilt.
		 */
		reapplyAllPendingEdits(): void {
			if (!view) return;
			Object.entries(pendingEditsRef.current.layers).forEach(
				([layerId, edits]) => {
					// Bubble layers are owned by the single sensor producer — route
					// them there so per-band colour fills in (after the lookup) and
					// the glow survives reload/save without the diamond path racing.
					if (edits.type === 'bubble') {
						applySensorStyleNow(layerId);
						return;
					}
					applyLayerEditsToMap(
						view,
						layerId,
						edits,
						buildTileColorExpressionRef.current,
						symbolLoaderRef.current,
						// Pass the sprite scheduler so symbol (icon) layers coloured
						// by value re-rasterise their per-band sprites once the id →
						// value lookup arrives (the colour expression is only built
						// after the lookup loads). Without it the icon-image would
						// reference sprites that were never generated → blank icons.
						spriteSchedulerRef.current?.schedule,
						getIconDataUrl,
						isTileLookupPendingRef.current,
						ensureSymbolSpritesRef.current,
						'reapplyAllPendingEdits',
					);
				},
			);
		},

		// ------------------------------------------------------------------
		// Edit accumulation — called by EsriMapInner on every onLayerEdit
		// ------------------------------------------------------------------

		accumulateEdit(layerId: string, edits: Partial<ConfigLayerInternal>): void {
			// Strip structural, computed, and runtime fields that must never be
			// persisted as theme edits. The field list is shared with the three other
			// persistence stages (getDifferencesFromDefaults, processBody,
			// responseParser) so they cannot drift apart again.
			const persistable = stripLayerFields(
				edits,
				NON_PERSISTABLE_LAYER_FIELDS,
			);
			const updated = {
				...pendingEditsRef.current.layers,
				[layerId]: { ...pendingEditsRef.current.layers[layerId], ...persistable },
			};
			setPendingLayers(updated);
		},

		// ------------------------------------------------------------------
		// Theme CRUD — wired to ThemeManager / backend
		// ------------------------------------------------------------------

		/**
		 * Saves the current pending edits to the backend.
		 * Derives isUserTheme from internal themeSettings state (shared → tenant theme).
		 * Clears isCreating and refreshes the theme list after save.
		 */
		async saveTheme(): Promise<void> {
			if (!themeManagerRef.current) {
				logger.warn(
					'[useTheme] saveTheme called but ThemeManager is not initialised — ' +
					'check that themeEndpoint.url is provided.',
				);
				return;
			}
			const isUserThemeValue = !themeSettings.shared;
			await themeManagerRef.current?.saveTheme(
				pendingEditsRef.current as any,
				themeNameRef.current,
				isUserThemeValue,
			);
			setIsCreating(false);
			// Update selectedThemeId to the real ID (replaces 'new' after first save)
			const newId = themeManagerRef.current?.getSelectedThemeId() ?? fallbackDefaultThemeId;
			setSelectedThemeId(newId);
			const list = await themeManagerRef.current?.getThemeList();
			if (list) setThemes(list);
		},

		/**
		 * Enters "creating new theme" mode.
		 * Immediately adds a 'new' placeholder to the themes dropdown.
		 */
		createTheme(): void {
			setPendingLayers({});
			setThemeName('');
			themeManagerRef.current?.createTheme();
			setIsCreating(true);
			setSelectedThemeId('new');
			// ThemeManager._themeList is mutated synchronously by createTheme() —
			// getThemeList() returns the updated in-memory list immediately (no network).
			themeManagerRef.current?.getThemeList().then(list => {
				if (list) setThemes(list);
			});
		},

		async deleteTheme(): Promise<void> {
			await themeManagerRef.current?.deleteTheme();
			themeManagerRef.current?.useDefaultTheme();
			const newId = themeManagerRef.current?.getSelectedThemeId() ?? fallbackDefaultThemeId;
			setSelectedThemeId(newId);
			const list = await themeManagerRef.current?.getThemeList();
			if (list) {
				setThemes(list);
				// Sync themeName to the newly-selected theme's actual name. Without
				// this, LayerPanel's themeList memo — which relabels whichever entry
				// matches selectedTheme with the live-edit themeName — keeps showing
				// the just-deleted theme's stale name on the new selection, since
				// themeName is otherwise only refreshed by changeTheme()/saveTheme().
				const newThemeName = list.find(t => t.id === newId)?.name;
				setThemeName(typeof newThemeName === 'string' ? newThemeName : '');
			}
		},

		/**
		 * Cancels theme creation.
		 * Removes the 'new' placeholder from the dropdown and restores prior selection.
		 */
		cancelCreating(): void {
			themeManagerRef.current?.cancelCreating();
			setIsCreating(false);
			const restoredId = themeManagerRef.current?.getSelectedThemeId() ?? fallbackDefaultThemeId;
			setSelectedThemeId(restoredId);
			// ThemeManager._themeList is mutated synchronously by cancelCreating() —
			// getThemeList() returns the updated in-memory list immediately (no network).
			themeManagerRef.current?.getThemeList().then(list => {
				if (list) setThemes(list);
			});
		},

		/**
		 * Resets the current theme to empty ConfigEdits (all style overrides removed).
		 * Wired to EditorProvider.onEditorReset.
		 */
		resetTheme(): void {
			themeManagerRef.current?.resetTheme();
			setPendingLayers({});
		},

		/**
		 * Switches to a different theme:
		 * 1. Updates ThemeManager selection (+ localStorage)
		 * 2. Fetches ConfigEdits from backend
		 * 3. Seeds themeName from the loaded ThemeProperties
		 * 4. Re-applies all renderers from the loaded edits
		 * 5. Resets pendingEditsRef to the loaded state
		 */
		async changeTheme(themeId: string, baseConfig?: ConfigLayer[]): Promise<void> {
			if (!themeManagerRef.current || !view) return;
			themeManagerRef.current.themeId = themeId;
			setSelectedThemeId(themeId);

			// Revert any layers the OUTGOING theme touched back to their
			// captured base style. Without this, when switching to a theme
			// that does not edit a layer the previous theme styled, stale
			// paint from the previous theme remains on the map.
			const outgoingLayerIds = Object.keys(
				pendingEditsRef.current.layers ?? {},
			);
			const layerManager = layerManagerRef.current;
			if (layerManager) {
				for (const layerId of outgoingLayerIds) {
					const composite = layerManager.compositeLayers.find(
						c => c.id === layerId,
					);
					if (!composite) continue;
					// Restore base for the main style layer id plus the
					// selected / highlight variants.
					const variantIds = [
						layerId,
						`${layerId}-selected`,
						`${layerId}-highlight-background`,
						`${layerId}-highlight-foreground`,
					];
					for (const variantId of variantIds) {
						restoreLayerToBaseStyle(composite, variantId);
					}
				}
			}

		const themeProps = await themeManagerRef.current.getById(themeId);

		// Seed every layer from its resolved base values, stripped of the fields
		// the theme pipeline must never treat as an edit, then let the incoming
		// theme's own edits win. See buildThemeSeedLayers for why the seed is
		// stripped with the read-gate list.
		const mergedLayers = buildThemeSeedLayers(
			baseConfig,
			themeProps?.configEdits?.layers ?? {},
		);

		if (themeProps?.configEdits) {
			const isUserTheme = themeProps.isUserTheme ?? true;
			setIsUserTheme(isUserTheme);
			setThemeSettings({ shared: !isUserTheme });
			setThemeName(typeof themeProps.name === 'string' ? themeProps.name : '');
			setPendingLayers({ ...mergedLayers });
			setLoadedThemeEdits({ ...mergedLayers });
		}
		},

		// Expose isUserTheme so EsriMapInner can derive editorState
		isUserTheme,

		// Ref to the pending edits accumulator — consumers can read .current.layers
		// synchronously without waiting for a React re-render (e.g. style-loaded callbacks).
		pendingEditsRef,

		// Pending edits as state — drives display config merging in EsriMapInner
		pendingEdits,
		// Full theme edits as loaded from backend — used by ReactEsriMap to push
		// into ConfigAdaptor so convertForPanel() returns correct iconImage.
		loadedThemeEdits,
	};
};

export type ThemeModule = ReturnType<typeof useTheme>;
